// =============================================================================
// features/sales/agenda/filters.ts — Filtro de campanha (client-side) +
// agrupamento por dia (modo calendário). Funções puras — ver
// __tests__/filters.test.ts.
//
// Por que "campanha" é um filtro client-side: o contrato de API (F33-S02)
// só filtra por `campaignMemberId` (uuid exato) — não existe ainda um
// endpoint de campanhas com nome (F34/F35, fora do escopo deste slot). Até
// lá, o filtro de campanha é binário (tem vínculo / não tem) e roda sobre a
// página já carregada — decisão registrada no PR, não é lacuna silenciosa.
// AppointmentList mantém a paginação visível/navegável quando esse filtro
// zera a página atual (achado de revisão F33-S05).
//
// "Situação" (Concluído/Cancelado) NÃO é filtrada aqui — a API já suporta
// `status` nativamente, então AgendaContent.tsx manda o filtro no próprio
// request (ver `statusOverride`). `SituacaoFilter`/`SITUACAO_FILTER_OPTIONS`
// continuam aqui só como o vocabulário compartilhado do dropdown de UI.
// =============================================================================

import type { Appointment } from '@elemento/shared-schemas';

import { dayKeyOrgTz, formatWeekdayDateOrgTz } from './timezone';

// ---------------------------------------------------------------------------
// Campanha (client-side — ver nota de cabeçalho)
// ---------------------------------------------------------------------------

export type CampaignFilter = 'all' | 'with' | 'without';

export const CAMPAIGN_FILTER_OPTIONS: { value: CampaignFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'with', label: 'Com campanha' },
  { value: 'without', label: 'Sem campanha' },
];

export function applyCampaignFilter(items: Appointment[], filter: CampaignFilter): Appointment[] {
  if (filter === 'all') return items;
  if (filter === 'with') return items.filter((a) => a.campaignMemberId !== null);
  return items.filter((a) => a.campaignMemberId === null);
}

// ---------------------------------------------------------------------------
// Situação (sub-filtro visível só na aba Concluídos) — vocabulário de UI.
// A filtragem em si é server-side (ver cabeçalho do arquivo).
// ---------------------------------------------------------------------------

export type SituacaoFilter = 'all' | 'completed' | 'cancelled';

export const SITUACAO_FILTER_OPTIONS: { value: SituacaoFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
];

// ---------------------------------------------------------------------------
// Agrupamento por dia — modo "calendário" (doc 26 §6.1: primeira entrega
// pode renderizar a lista agrupada por dia; calendário rico é follow-up).
// ---------------------------------------------------------------------------

export interface AppointmentDayGroup {
  dayKey: string;
  label: string;
  items: Appointment[];
}

export function groupByDay(items: Appointment[]): AppointmentDayGroup[] {
  const groups = new Map<string, Appointment[]>();
  for (const item of items) {
    const key = dayKeyOrgTz(item.scheduledAt);
    const arr = groups.get(key) ?? [];
    arr.push(item);
    groups.set(key, arr);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, groupItems]) => ({
      dayKey,
      label: formatWeekdayDateOrgTz(groupItems[0]?.scheduledAt ?? new Date().toISOString()),
      items: groupItems,
    }));
}
